import i18next from 'i18next';
import { useQuery } from 'react-query';
import { InvoiceInterface } from '../../../interfaces/invoice.interface';
import { PAYMENT_METHOD_AADE_CODE } from '../../../interfaces/paymentTypes.interface';
import { VIVA_SESSION_DETAIL_MESSAGE, VivaSessionResponse } from '../../../interfaces/provider.interface';
import { setToast } from '../../../store/core/coreSlice';
import { store } from '../../../store/store';
import { apiRQ } from '../../api';
import { queryClient } from '../../APIProvider';

const POLLING_INTERVAL_6_SEC = 6000;
const POLLING_INTERVAL_10_SEC = 10000;

export const isProviderSaleFailure = (invoiceId: string | undefined, invoice: InvoiceInterface | undefined | null) => {
  if (!invoice) return false;

  return !!invoiceId && !!invoice?.post_with_provider && !!invoice?.sentToAade && !invoice?.mark && !!invoice?.failedAt;
};

/**
 * Hook should only be enabled if:
 * - invoiceId is defined
 * - invoice is defined and has `post_with_provider` set to true
 * - invoice is sent to AADE from a user action (`sentToAade` is true)
 * - invoice is not marked as posted (`mark` is undefined/null)
 * - invoice is not marked as failed (`failedAt` is undefined/null)
 */
export const isProviderSalePending = (invoiceId: string | undefined, invoice: InvoiceInterface | undefined | null) => {
  return !!invoiceId && !!invoice?.post_with_provider && !!invoice?.sentToAade && !invoice?.mark && !invoice?.failedAt;
};

export const isProviderSaleStatusPending = (data: VivaSessionResponse | undefined | null) => {
  return data?.detail === VIVA_SESSION_DETAIL_MESSAGE.IN_PROGRESS;
};

export const refetchOptions = (
  data: VivaSessionResponse | undefined | null,
  invoice: InvoiceInterface | undefined | null,
) => {
  const isStatusPending = isProviderSaleStatusPending(data);
  let refetchTimeout = POLLING_INTERVAL_6_SEC;
  if (invoice?.paymentType?.aadeCode === PAYMENT_METHOD_AADE_CODE.POS) {
    refetchTimeout = POLLING_INTERVAL_10_SEC; // POS transactions might take longer, so we double the interval
  }

  return { shouldRefetch: isStatusPending, refetchTimeout };
};

const getSaleStateViva = async (invoiceId: string) => {
  const resp = await apiRQ.get<VivaSessionResponse>(`/viva/sale/${invoiceId}`);
  return resp.data;
};

/**
 * Request the sale state from Viva.
 * This should only be enabled when the invoice is:
 *  - marked as `post_with_provider`
 *  - sent to aade from a user action (`sentToAade` is true)
 *  - not marked as posted (`mark` is undefined/null)
 * Refetch should only be triggered if the last response detail is `IN_PROGRESS`.
 */
export const useSaleState = (invoiceId: string | undefined, invoice: InvoiceInterface | undefined | null) => {
  return useQuery<VivaSessionResponse, Error>(
    ['useSaleState', invoiceId, invoice?.post_with_provider, invoice?.sentToAade],
    () => getSaleStateViva(invoiceId || ''),
    {
      enabled: isProviderSalePending(invoiceId, invoice),
      cacheTime: 1000,
      staleTime: 1000,
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (data) => {
        // Sale was processed
        if (!isProviderSaleStatusPending(data)) {
          // A known AADE validation error (e.g. a stale ship date) - the response interceptor
          // (`api.ts`) already shows a specific toast for `data.errorCode` on every response, so
          // showing the generic one here too would stack two toasts for the same failure.
          if (data?.success === false && !data?.errorCode) {
            const errorMsg = i18next.t('API_ERRORS.UNABLE_TO_POST_ENTITY_TO_MY_DATA_PROVIDER');

            store.dispatch(
              setToast({
                value: { show: true, msg: errorMsg, isError: true },
              }),
            );
          }

          // In this flow the Viva session has been resolved, which means that BE has updated the invoice
          // We need to invalidate the query to ensure that the UI reflects the latest state of Invoice
          if (isProviderSalePending(invoiceId, invoice)) {
            // We are adding a small delay in order to get directly the Provider response
            // since if the pos terminal app is open the session resolves immediately
            setTimeout(() => {
              queryClient?.invalidateQueries({ queryKey: ['getSale', invoiceId] });
            }, 1000);
          }
        }
      },
      refetchInterval: (data) => {
        if (!isProviderSalePending(invoiceId, invoice) || !data) return false; // Disable polling if the hook is not enabled

        // Only continue polling if the session is still being processed
        const refetchData = refetchOptions(data, invoice);
        if (refetchData.shouldRefetch) {
          return refetchData.refetchTimeout || POLLING_INTERVAL_6_SEC;
        }

        return false; // Stop polling
      },
    },
  );
};
